import React, { FormEvent, useState, useEffect } from "react";
import { Package, AlertTriangle, Image, FileText, MapPin, Plus, X } from "lucide-react";
import { ValidationErrors } from "src/errors";
import {
  ClaimFormDataKey,
  ClaimFormData,
  ClaimFormAction,
  ClaimType,
  ClaimUrgencyLevel,
  ClaimDetailInputField,
  ClaimNonOrderProductInputField,
  DAMAGE_CLAIM_TYPES,
  DISCREPANCY_CLAIM_TYPES,
  CLAIM_URGENCY_LEVELS
} from "src/types";
import { PurchaseOrder, TransitPurchaseOrder } from "src/types/database";
import { useScrollOnMessage } from "src/hooks";
import { TextInputBox, TextInputBoxLarge, SelectInputBox, MultiFileUpload } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { ClaimProductInputTable, ClaimNonOrderProductInputTable } from "src/components/tables";
import { ClaimMapper } from "src/mappers";
import { CollapsibleSection } from "src/components/ui/collapsible";

interface CreateClaimFormProps {
  formData: ClaimFormData;
  purchaseOrder?: PurchaseOrder;
  transitPurchaseOrder?: TransitPurchaseOrder;
  validationErrors?: ValidationErrors<ClaimFormDataKey>;
  formLvlErrorMessage: string;
  handleInputChange: (field: ClaimFormDataKey, value: ClaimFormData[keyof ClaimFormData]) => void;
  handleSubmit: (e: FormEvent) => Promise<void>;
  setAction: (action: ClaimFormAction) => void;
}

const getClaimTypes = (issueType: "damage" | "discrepancy"): ClaimType[] => {
  return issueType === "damage" ? [...DAMAGE_CLAIM_TYPES] : [...DISCREPANCY_CLAIM_TYPES];
};

const CreateClaimForm: React.FC<CreateClaimFormProps> = ({
  formData,
  purchaseOrder,
  transitPurchaseOrder,
  validationErrors,
  formLvlErrorMessage,
  handleInputChange,
  handleSubmit,
  setAction
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const [claimDetailStates, setClaimDetailStates] = useState<Map<string, boolean>>(new Map());

  useEffect(() => {
    setClaimDetailStates((prev) => {
      const newStates = new Map(prev);
      formData.claimDetails.forEach((detail) => {
        if (!newStates.has(detail.fieldId)) {
          newStates.set(detail.fieldId, false);
        }
      });
      const currentFieldIds = new Set(formData.claimDetails.map((d) => d.fieldId));
      newStates.forEach((_, fieldId) => {
        if (!currentFieldIds.has(fieldId)) {
          newStates.delete(fieldId);
        }
      });
      return newStates;
    });
  }, [formData.claimDetails]);

  const handleSetProducts: React.Dispatch<React.SetStateAction<typeof formData.products>> = (updatedProducts) => {
    handleInputChange("products", updatedProducts as typeof formData.products);
  };

  const handleAddClaimDetail = () => {
    const newDetail = ClaimMapper.initClaimDetailInputField(formData.issueType);
    handleInputChange("claimDetails", [...formData.claimDetails, newDetail]);
  };

  const handleRemoveClaimDetail = (fieldId: string) => {
    if (formData.claimDetails.length > 1) {
      handleInputChange(
        "claimDetails",
        formData.claimDetails.filter((detail) => detail.fieldId !== fieldId)
      );
      setClaimDetailStates((prev) => {
        const next = new Map(prev);
        next.delete(fieldId);
        return next;
      });
    }
  };

  const handleClaimDetailToggle = (fieldId: string, isOpen: boolean) => {
    setClaimDetailStates((prev) => {
      const next = new Map(prev);
      next.set(fieldId, isOpen);
      return next;
    });
  };

  const getClaimDetailState = (fieldId: string): boolean => {
    return claimDetailStates.get(fieldId) ?? false;
  };

  const handleClaimDetailChange = (
    fieldId: string,
    field: keyof ClaimDetailInputField,
    value: string | ClaimType | ClaimUrgencyLevel | ClaimNonOrderProductInputField[]
  ) => {
    handleInputChange(
      "claimDetails",
      formData.claimDetails.map((detail) => (detail.fieldId === fieldId ? { ...detail, [field]: value } : detail))
    );
  };

  const handleAddNonOrderProduct = (detailFieldId: string) => {
    const newProduct = ClaimMapper.initClaimNonOrderProductInputField();
    handleClaimDetailChange(detailFieldId, "nonOrderProducts", [
      ...(formData.claimDetails.find((d) => d.fieldId === detailFieldId)?.nonOrderProducts || []),
      newProduct
    ]);
  };

  const isWrongProductClaimType = (claimType: ClaimType): boolean => {
    return claimType === "Wrong Product";
  };

  const getClaimDetailError = (detailIndex: number, fieldName?: string): string | undefined => {
    const errorMessage = validationErrors?.getErrorMessageByFormDataKey("claimDetails");
    if (!errorMessage) return undefined;

    if (errorMessage.includes("At least one claim detail with description is required")) {
      if (fieldName === "detailedDescription" && detailIndex === 0) {
        return errorMessage;
      }
      return undefined;
    }

    const detailMatch = errorMessage.match(/Claim detail (\d+):/i);
    if (!detailMatch) {
      return undefined;
    }

    const errorDetailIndex = parseInt(detailMatch[1], 10) - 1;
    if (detailIndex !== errorDetailIndex) {
      return undefined;
    }

    if (fieldName) {
      const fieldPatterns: Record<string, RegExp> = {
        claimType: /claim type/i,
        urgencyLevel: /urgency level/i,
        detailedDescription: /detailed description|description/i
      };
      const fieldPattern = fieldPatterns[fieldName];
      if (fieldPattern && !fieldPattern.test(errorMessage)) return undefined;
    }

    return errorMessage.replace(/^Claim detail \d+: /i, "");
  };

  const getNonOrderProductError = (
    detailIndex: number,
    productIndex: number,
    fieldName: "productId" | "claimQuantity" | "extraInfo"
  ): string | undefined => {
    const errorMessage = validationErrors?.getErrorMessageByFormDataKey("claimDetails");
    if (!errorMessage) return undefined;

    const fieldLabel =
      fieldName === "productId" ? "Product ID" : fieldName === "claimQuantity" ? "Claim quantity" : "Extra info";
    const productMatch = errorMessage.match(
      new RegExp(
        `Claim detail (\\d+), Non-order product (\\d+):\\s*(.+?${fieldLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}.+?)(?:\\.|$)`,
        "i"
      )
    );

    if (productMatch) {
      const errorDetailIndex = parseInt(productMatch[1], 10) - 1;
      const errorProductIndex = parseInt(productMatch[2], 10) - 1;

      if (detailIndex === errorDetailIndex && productIndex === errorProductIndex) {
        return productMatch[3]?.trim();
      }
    }

    const simpleMatch = errorMessage.match(new RegExp(`Claim detail (\\d+), Non-order product (\\d+):\\s*(.+)`, "i"));

    if (simpleMatch) {
      const errorDetailIndex = parseInt(simpleMatch[1], 10) - 1;
      const errorProductIndex = parseInt(simpleMatch[2], 10) - 1;

      if (detailIndex === errorDetailIndex && productIndex === errorProductIndex) {
        const message = simpleMatch[3]?.trim() || "";
        if (fieldName === "productId" && message.includes("Product ID")) {
          return message;
        }
        if (fieldName === "claimQuantity" && message.includes("Claim quantity")) {
          return message;
        }
        if (fieldName === "extraInfo" && message.includes("Extra info")) {
          return message;
        }
      }
    }

    return undefined;
  };

  const getNonOrderProductsGeneralError = (detailIndex: number): string | undefined => {
    const errorMessage = validationErrors?.getErrorMessageByFormDataKey("claimDetails");
    if (!errorMessage) return undefined;

    if (errorMessage.includes("non-order product is required")) {
      const detailMatch = errorMessage.match(/Claim detail (\d+):/i);
      if (detailMatch) {
        const errorDetailIndex = parseInt(detailMatch[1], 10) - 1;
        if (detailIndex === errorDetailIndex) {
          return errorMessage.replace(/^Claim detail \d+: /i, "");
        }
      }
    }

    return undefined;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">
              Create {formData.issueType === "discrepancy" ? "Discrepancy" : "Damage"} Claim
            </h1>
            <p className="text-gray-300 mt-2 text-lg">Report warehouse claim incidents</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Purchase Order ID Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Package className="w-4 h-4 mr-2" />
                  Purchase Order ID
                </h3>
                <TextInputBox
                  id="purchaseOrderId"
                  placeholder="Purchase Order ID"
                  onChange={(value) => handleInputChange("purchaseOrderId", value)}
                  value={formData.purchaseOrderId}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("purchaseOrderId")} />
              </div>

              {(purchaseOrder || transitPurchaseOrder) && (
                <>
                  {/* Products Section */}
                  {formData.products.length > 0 && (
                    <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                      <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                        <FileText className="w-4 h-4 mr-2" />
                        Products
                      </h3>
                      <ClaimProductInputTable
                        products={formData.products}
                        setProducts={handleSetProducts}
                        minProducts={0}
                      />
                      <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("products")} />
                    </div>
                  )}

                  {/* Claim Information Section - Show if there are regular products */}
                  {formData.products.length > 0 && (
                    <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                      <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Claim Information
                      </h3>
                      <div className="bg-secondary-50 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
                            Claim Price
                          </span>
                        </div>
                        <p className="text-lg font-semibold text-secondary-900">
                          £
                          {formData.products
                            .reduce((total, product) => {
                              const qtyClaim = parseInt(product.claimQuantity, 10) || 0;
                              return total + qtyClaim * product.unitPrice;
                            }, 0)
                            .toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Claim Details Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-black tracking-wide uppercase flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Claim Details
                      </h3>
                      <button
                        type="button"
                        onClick={handleAddClaimDetail}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add More Claim Detail
                      </button>
                    </div>
                    {formData.claimDetails.map((detail, index) => {
                      const isCollapsible = formData.claimDetails.length > 1;
                      const showNonOrderProducts = isWrongProductClaimType(detail.claimType);
                      const nonOrderProducts = detail.nonOrderProducts || [];

                      const detailContent = (
                        <div className="p-4 bg-secondary-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <SelectInputBox
                                id={`claimType-${detail.fieldId}`}
                                label="Claim Type"
                                value={detail.claimType}
                                options={getClaimTypes(formData.issueType)}
                                onChange={(value) => {
                                  const newClaimType = value as ClaimType;
                                  const isWrongProduct = isWrongProductClaimType(newClaimType);

                                  // Update both claimType and nonOrderProducts in a single state update
                                  handleInputChange(
                                    "claimDetails",
                                    formData.claimDetails.map((d) =>
                                      d.fieldId === detail.fieldId
                                        ? {
                                            ...d,
                                            claimType: newClaimType,
                                            // Initialize nonOrderProducts if switching to wrong product type and it doesn't exist
                                            nonOrderProducts:
                                              isWrongProduct && !d.nonOrderProducts ? [] : d.nonOrderProducts
                                          }
                                        : d
                                    )
                                  );
                                }}
                                required
                                error={getClaimDetailError(index, "claimType")}
                              />
                            </div>
                            <div>
                              <SelectInputBox
                                id={`urgencyLevel-${detail.fieldId}`}
                                label="Urgency Level"
                                value={detail.urgencyLevel}
                                options={[...CLAIM_URGENCY_LEVELS]}
                                onChange={(value) =>
                                  handleClaimDetailChange(detail.fieldId, "urgencyLevel", value as ClaimUrgencyLevel)
                                }
                                required
                                error={getClaimDetailError(index, "urgencyLevel")}
                              />
                            </div>
                          </div>
                          <div className="mb-4">
                            <TextInputBoxLarge
                              id={`detailedDescription-${detail.fieldId}`}
                              label="Detailed Description"
                              placeholder="Detailed Description (5-2000 characters)"
                              onChange={(value) =>
                                handleClaimDetailChange(detail.fieldId, "detailedDescription", value)
                              }
                              value={detail.detailedDescription}
                              maxLength={2000}
                              rows={5}
                            />
                            <ValidationErrorMessage message={getClaimDetailError(index, "detailedDescription")} />
                          </div>

                          {/* Non-Order Products Section - Only shown for Wrong Product claim types */}
                          {showNonOrderProducts && (
                            <div className="mt-6 pt-6 border-t border-secondary-200">
                              <div className="flex items-center justify-between mb-4">
                                <h4 className="text-sm font-semibold text-secondary-900 uppercase tracking-wide">
                                  Non-Order Products
                                </h4>
                                <button
                                  type="button"
                                  onClick={() => handleAddNonOrderProduct(detail.fieldId)}
                                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-colors"
                                >
                                  <Plus className="w-4 h-4" />
                                  Add Product
                                </button>
                              </div>
                              {nonOrderProducts.length === 0 ? (
                                <div>
                                  {getNonOrderProductsGeneralError(index) && (
                                    <div className="mb-3 p-3 bg-error-50 border border-error-200 rounded-lg">
                                      <p className="text-sm text-error-700">{getNonOrderProductsGeneralError(index)}</p>
                                    </div>
                                  )}
                                  <p className="text-sm text-secondary-600 italic">No non-order products added yet.</p>
                                </div>
                              ) : (
                                <ClaimNonOrderProductInputTable
                                  nonOrderProducts={nonOrderProducts}
                                  setNonOrderProducts={(updatedProducts) => {
                                    const productsArray =
                                      typeof updatedProducts === "function"
                                        ? updatedProducts(nonOrderProducts)
                                        : updatedProducts;
                                    handleClaimDetailChange(
                                      detail.fieldId,
                                      "nonOrderProducts",
                                      productsArray as ClaimNonOrderProductInputField[]
                                    );
                                  }}
                                  minNonOrderProducts={0}
                                  getProductError={(productIndex, fieldName) =>
                                    getNonOrderProductError(index, productIndex, fieldName)
                                  }
                                  generalError={getNonOrderProductsGeneralError(index)}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      );

                      if (isCollapsible) {
                        return (
                          <div key={detail.fieldId} className={index < formData.claimDetails.length - 1 ? "mb-4" : ""}>
                            <CollapsibleSection
                              title={
                                <div className="flex items-center justify-between w-full min-w-0">
                                  <div className="flex items-center space-x-3 min-w-0">
                                    <span className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-800 rounded-full flex items-center justify-center text-sm font-semibold">
                                      {index + 1}
                                    </span>
                                    <span className="text-sm text-secondary-700 truncate">
                                      {detail.claimType || `Claim Detail ${index + 1}`}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                                    <button
                                      type="button"
                                      className="inline-flex items-center p-1.5 text-error-600 hover:text-error-800 hover:bg-error-50 rounded-md transition-colors duration-200"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveClaimDetail(detail.fieldId);
                                      }}
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              }
                              isOpen={getClaimDetailState(detail.fieldId)}
                              onToggle={(isOpen) => handleClaimDetailToggle(detail.fieldId, isOpen)}
                              variant="minimal"
                            >
                              {detailContent}
                            </CollapsibleSection>
                          </div>
                        );
                      }

                      // Single detail - not collapsible
                      return (
                        <div
                          key={detail.fieldId}
                          className="p-4 border border-secondary-200 rounded-lg bg-secondary-50"
                        >
                          {detailContent}
                        </div>
                      );
                    })}
                  </div>

                  {/* Warehouse Location Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      Warehouse Location
                    </h3>
                    <TextInputBox
                      id="warehouseLocation"
                      placeholder="Warehouse Location"
                      onChange={(value) => handleInputChange("warehouseLocation", value)}
                      value={formData.warehouseLocation}
                      required
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("warehouseLocation")}
                    />
                  </div>

                  {/* Images Upload Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <Image className="w-4 h-4 mr-2" />
                      Claim Images
                    </h3>
                    <MultiFileUpload
                      uploadText="Upload Images (Optional)"
                      uploadedFiles={formData.claimImages}
                      onFileChange={(files) => handleInputChange("claimImages", files ?? [])}
                      fileFormat="image/jpeg, image/png, image/gif, image/webp"
                    />
                    <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("claimImages")} />
                  </div>
                </>
              )}

              {/* Form Level Error */}
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {/* Submit Button */}
              <div className="flex items-center gap-4">
                <button
                  className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                  disabled={!formData.purchaseOrderId}
                  onClick={() => setAction(!purchaseOrder && !transitPurchaseOrder ? "FETCH" : "SUBMIT")}
                  type="submit"
                >
                  {!purchaseOrder && !transitPurchaseOrder ? "Search" : "Submit Claim"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { CreateClaimForm };
